import React, { useCallback, useEffect, useMemo } from 'react';
import get from 'lodash/get';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';

import { AlertInfo } from '@ad-scripts/presentation/components/common/alert';
import { Button, ControlLabel } from 'src/presentation/components/agent_desktop_components';
import CompanyPhoneNumber from 'src/models/CompanyPhoneNumber/CompanyPhoneNumber';
import Dropdown from 'src/presentation/components/forms/Dropdown';
import EmbedModalWithGreenHeader from 'src/presentation/components/pages/shared/EmbedModalWithGreenHeader';
import FieldContainer from 'src/presentation/components/forms/FieldContainer';
import Form, { useForm } from 'src/presentation/components/forms/Form';
import { formatPhone } from 'src/actions/shared/phonesHelper';
import { HeaderItem } from '@ad-scripts/presentation/components/common/dropdown_menu';
import InputField from 'src/presentation/components/forms/InputField';
import Menu from '@ad-scripts/presentation/components/common/menu';
import { ModalSize } from 'src/presentation/components/pages/shared/EmbedModal';
import PhoneNumberInput from 'src/presentation/components/forms/PhoneNumberInput';
import RadioButton from '@ad-scripts/presentation/components/common/radio_button';
import { Spinner } from 'src/presentation/components/pages/shared/Spinner';
import { StyledExternalLink } from 'src/presentation/components/pages/shared/ExternalLink';
import useAvailableNumbersForWhatsApp from 'src/presentation/components/pages/channels/whatsapp/hooks/useAvailableNumbersForWhatsApp';
import whatsAppSenderResource, { WhatsAppConfigurationFields } from 'src/actions/channels/whatsAppSenderResource';
import { RootState } from 'src/actions/configuration/rootReducer';
import { WhatsAppSender } from 'src/models/Channels/WhatsAppSender';

type ConfigureWhatsAppModalProps = {
  handleCloseModal: () => void;
  initiateFBLogin: (onlyWabaSharing: boolean) => void;
  setNewSenderInfo: React.Dispatch<React.SetStateAction<WhatsAppSender | undefined>>;
};

const initialState: WhatsAppConfigurationFields = {
  displayName: '',
  isGladlyNumber: true,
  ownPhoneNumber: '',
  gladlyPhoneNumber: '',
  phoneSid: '',
  availableNumbers: [],
  usedNumbers: [],
};

const isFormFilledIn = (workingState: WhatsAppConfigurationFields | undefined) => {
  if (!workingState) return false;
  if (workingState.isGladlyNumber) {
    return !!workingState.gladlyPhoneNumber && !!workingState.displayName && !!workingState.phoneSid;
  }
  return !!workingState.ownPhoneNumber;
};

function useOnSave(
  initiateFBLogin: (onlyWabaSharing: boolean) => void,
  handleCloseModal: () => void,
  setNewSenderInfo: React.Dispatch<React.SetStateAction<WhatsAppSender | undefined>>,
  validate: any
) {
  const dispatch = useDispatch();
  return ({ workingState }) => {
    if (!workingState || !dispatch(validate(workingState))) {
      return;
    }

    const newSenderInfo: WhatsAppSender = new WhatsAppSender(workingState);
    setNewSenderInfo(newSenderInfo);
    initiateFBLogin(workingState.isGladlyNumber);
    handleCloseModal();
  };
}

const ConfigureWhatsAppModal = ({
  handleCloseModal,
  initiateFBLogin,
  setNewSenderInfo,
}: ConfigureWhatsAppModalProps) => {
  const dispatch = useDispatch();
  const { resetErrors } = whatsAppSenderResource.actions;
  const { validateNonGladlyPhoneNumber } = whatsAppSenderResource.effects;
  const saveHandler = useOnSave(initiateFBLogin, handleCloseModal, setNewSenderInfo, validateNonGladlyPhoneNumber);
  const errors = useSelector((state: RootState) => state.whatsAppSender.errors);
  const formContext = useForm<WhatsAppConfigurationFields>({
    item: initialState,
    resetErrors,
    onSave: saveHandler,
    errors,
  });
  const { updateState, workingState } = formContext;
  const { gladlyPhoneNumber, ownPhoneNumber, displayName, isGladlyNumber } = workingState;
  const { availableNumbers, usedNumbers, isLoading: isLoadingAvailableNumbers } = useAvailableNumbersForWhatsApp();

  const gladlyNumberOptions = useMemo(
    () =>
      availableNumbers.map((companyNumber: CompanyPhoneNumber) => ({
        label: formatPhone(companyNumber.phoneNumber),
        value: companyNumber.phoneNumber,
      })),
    [availableNumbers]
  );

  useEffect(() => {
    updateState({ availableNumbers: availableNumbers, usedNumbers: usedNumbers });
  }, [availableNumbers, usedNumbers, updateState]);

  useEffect(() => {
    dispatch(whatsAppSenderResource.actions.validationError([]));
  }, [gladlyPhoneNumber, ownPhoneNumber, displayName, isGladlyNumber, dispatch]);

  const numberToSidMap = availableNumbers.reduce((acc, companyNumber: CompanyPhoneNumber) => {
    acc[companyNumber.phoneNumber] = companyNumber.externalId;
    return acc;
  }, {});

  const onPhoneNumberSelectChange = useCallback(
    phoneNumber => {
      const updates = {
        gladlyPhoneNumber: phoneNumber,
        phoneSid: numberToSidMap[phoneNumber],
      };
      updateState(updates);
    },
    [numberToSidMap, updateState]
  );

  const onGladlyPhoneNumberRadioClick = useCallback(() => {
    updateState({ isGladlyNumber: true });
  }, [updateState]);

  const onOwnPhoneNumberRadioClick = useCallback(() => {
    updateState({ isGladlyNumber: false });
  }, [updateState]);

  return (
    <EmbedModalWithGreenHeader
      data-aid="configure-whatsapp"
      title="Connect WhatsApp"
      modalSize={ModalSize.LARGE}
      content={
        isLoadingAvailableNumbers ? (
          <Spinner />
        ) : (
          ModalContent({
            formContext,
            onGladlyPhoneNumberRadioClick,
            onOwnPhoneNumberRadioClick,
            onPhoneNumberSelectChange,
            gladlyNumberOptions,
          })
        )
      }
      onCancel={handleCloseModal}
      disableOutsideClick={true}
    />
  );
};

function ModalContent({
  formContext,
  onGladlyPhoneNumberRadioClick,
  onOwnPhoneNumberRadioClick,
  onPhoneNumberSelectChange,
  gladlyNumberOptions,
}) {
  const { onSave, workingState } = formContext;
  const isFormValid = isFormFilledIn(workingState);

  return (
    <>
      <FormContainer>
        <Form context={formContext}>
          <OptionsContainer>
            <GladlyPhoneNumberSection
              formContext={formContext}
              onGladlyPhoneNumberRadioClick={onGladlyPhoneNumberRadioClick}
              onPhoneNumberSelectChange={onPhoneNumberSelectChange}
              gladlyNumberOptions={gladlyNumberOptions}
            />
            <OwnPhoneNumberSection formContext={formContext} onOwnPhoneNumberRadioClick={onOwnPhoneNumberRadioClick} />
          </OptionsContainer>
        </Form>
      </FormContainer>
      <StyledFooter>
        <Button data-aid="editor-save" disabled={!isFormValid} type={Button.Types.PRIMARY} onClick={onSave}>
          Next
        </Button>
      </StyledFooter>
    </>
  );
}

function GladlyPhoneNumberSection({
  formContext,
  onGladlyPhoneNumberRadioClick,
  onPhoneNumberSelectChange,
  gladlyNumberOptions,
}) {
  const { workingState } = formContext;
  const isGladlyNumber = get(workingState, 'isGladlyNumber');
  const gladlyPhoneNumber = get(workingState, 'gladlyPhoneNumber');
  const hasAvailableNumbers = gladlyNumberOptions.length > 0;
  const noAvailableNumbersHeader = [
    {
      label: 'You have no Gladly SMS-capable phone numbers.\nSet up an SMS or Voice number to get started.',
      type: 'HEADER',
    },
  ];
  return (
    <OptionContainer>
      <RadioButton isChecked={isGladlyNumber} onChange={onGladlyPhoneNumberRadioClick} />
      <NumberContainer>
        <StyledLablel>Connect a Gladly SMS-capable phone number</StyledLablel>
        {isGladlyNumber && (
          <FieldsContainer>
            <FieldContainer
              control={
                <PhoneNumberDropdown
                  attr="gladlyPhoneNumber"
                  data-aid="whatsapp-phone-number"
                  fitOpenerWidth={hasAvailableNumbers}
                  menuComponent={hasAvailableNumbers ? undefined : NoAvailableNumbersMenu}
                  onChange={onPhoneNumberSelectChange}
                  options={hasAvailableNumbers ? gladlyNumberOptions : noAvailableNumbersHeader}
                  placeholder="Select phone number"
                  searchable={gladlyNumberOptions.length > 15}
                />
              }
            />
            {gladlyPhoneNumber && (
              <>
                <FieldContainer
                  label="Display name"
                  description="Customers see this name when they view your WhatsApp Business profile."
                  control={
                    <DisplayNameInput attr="displayName" placeholder="Enter name" data-aid="whatsapp-display-name" />
                  }
                />
                <StyledAlertInfo>
                  <b>Heads up:&nbsp;</b>
                  Your display name must adhere to{' '}
                  <StyledExternalLink
                    label="WhatsApp Business display name guidelines"
                    to="https://www.facebook.com/business/help/338047025165344"
                  />
                  . Updating the name will require submitting a ticket to the WhatsApp Tech Provider.
                </StyledAlertInfo>
              </>
            )}
          </FieldsContainer>
        )}
      </NumberContainer>
    </OptionContainer>
  );
}

function OwnPhoneNumberSection({ formContext, onOwnPhoneNumberRadioClick }) {
  const { workingState } = formContext;
  const isGladlyNumber = get(workingState, 'isGladlyNumber');
  return (
    <OptionContainer>
      <RadioButton isChecked={!isGladlyNumber} onChange={onOwnPhoneNumberRadioClick} />
      <NumberContainer>
        <StyledLablel>Connect your own phone number</StyledLablel>
        {!isGladlyNumber && (
          <FieldContainer
            data-aid="whatsapp-phone-number"
            description="Enter your phone number in international format with spaces (e.g., +1 555-012-3456)."
            control={<StyledPhoneNumberInput data-aid="own-phone-number-input" attr="ownPhoneNumber" />}
          />
        )}
      </NumberContainer>
    </OptionContainer>
  );
}

const FormContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.insetLarge};
  padding-top: 0;
  padding-bottom: ${({ theme }) => theme.spacing.insetMedium};
  margin-bottom: ${({ theme }) => theme.spacing.insetLarge};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray300};
`;

const OptionContainer = styled.div`
  display: flex;
  align-items: start;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const NoAvailableNumbersMenu = styled(Menu)`
  width: 350px;

  ${HeaderItem} {
    color: ${({ theme }) => theme.colors.gray700};
    font-size: ${({ theme }) => theme.fontSize.base};
    font-weight: ${({ theme }) => theme.fontWeight.normal};
    line-height: ${({ theme }) => theme.lineHeight.base};
    padding: ${({ theme }) => theme.spacing.large};
    white-space: pre-line;
  }
`;

const PhoneNumberDropdown = styled(Dropdown)`
  max-width: 203px;
`;

const DisplayNameInput = styled(InputField)`
  max-width: 419px;
`;

const FieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-top: 2px;
`;

const StyledLablel = styled(ControlLabel)`
  margin-bottom: 0;
`;

const NumberContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 2px;
`;

const StyledAlertInfo = styled(AlertInfo)`
  line-height: 21px;
  text-align: left;

  svg {
    height: 24px;
  }
`;

const StyledFooter = styled.div`
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.insetLarge};
  padding-top: ${({ theme }) => theme.spacing.insetLarge};
  padding: 0 ${({ theme }) => theme.spacing.insetLarge};
`;

const StyledPhoneNumberInput = styled(PhoneNumberInput)`
  width: 258px;
  padding: 5px 8px;
  flex-shrink: 0;
`;

export default ConfigureWhatsAppModal;
